import { useState } from 'react'
import {
  Box, Typography, Paper, Table, TableHead, TableRow, TableCell, TableBody,
  TextField, Button, Stack, CircularProgress, Alert, InputAdornment, TablePagination,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import AddIcon from '@mui/icons-material/Add'
import * as clientesApi from '../../api/clientes'
import { useNotify } from '../../components/Notifications'
import ClienteAltaModal from './ClienteAltaModal'

export default function ClientesPage() {
  const notify = useNotify()
  const [search, setSearch] = useState('')
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [altaOpen, setAltaOpen] = useState(false)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)

  const buscar = async () => {
    setLoading(true); setError(null)
    try {
      // El typeahead requiere >= 3 caracteres (backend 500ea con menos). Ver clientes.js.
      const res = await clientesApi.buscarClientes(search)
      const list = Array.isArray(res) ? res : res?.list || res?.data || []
      setRows(list)
      setPage(0)
    } catch {
      setError('No se pudieron cargar los clientes (backend no disponible).')
      setRows([])
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="h5" fontWeight={600}>Clientes</Typography>
        <Button variant="contained" color="secondary" startIcon={<AddIcon />} onClick={() => setAltaOpen(true)}>Nuevo cliente</Button>
      </Stack>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2}>
          <TextField
            size="small" fullWidth placeholder="Buscar por nombre, RUC o código"
            value={search} onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && buscar()}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          />
          <Button variant="contained" onClick={buscar} disabled={loading}>Buscar</Button>
        </Stack>
      </Paper>

      {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper variant="outlined">
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 600 } }}>
                <TableCell>Código</TableCell>
                <TableCell>Nombre</TableCell>
                <TableCell>RUC / Documento</TableCell>
                <TableCell>Ciudad</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.secondary' }}>Buscá un cliente o creá uno nuevo.</TableCell></TableRow>
              )}
              {rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((c) => (
                <TableRow key={c.id} hover>
                  <TableCell>{c.codigo || '—'}</TableCell>
                  <TableCell>{c.nombre || c.nombreFantasia || '—'}</TableCell>
                  <TableCell>{c.numero || c.numeroDocumento || '—'}</TableCell>
                  <TableCell>{c.ciudad?.descripcion || '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {!loading && rows.length > 0 && (
          <TablePagination
            component="div" count={rows.length} page={page} onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0) }}
            rowsPerPageOptions={[10, 25, 50, 100]} labelRowsPerPage="Filas por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
          />
        )}
      </Paper>

      <ClienteAltaModal open={altaOpen} onClose={() => setAltaOpen(false)} onCreated={() => buscar()} />
    </Box>
  )
}
